import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CtaBanner } from "@/components/site/CtaBanner";
import { Reveal } from "@/components/site/Reveal";
import { ArrowRight, ChevronRight } from "lucide-react";
import { findCategory, usageCategories } from "@/lib/usage-data";

export const Route = createFileRoute("/usage/$category/")({
  loader: ({ params }) => {
    const category = findCategory(params.category);
    if (!category) throw notFound();
    return { category };
  },
  head: ({ loaderData, params }) => {
    const name = loaderData?.category.name ?? "Usage";
    const url = `https://blancopros.com/usage/${params.category}`;
    return {
      meta: [
        { title: `${name} Epoxy Flooring NJ | Blanco Pro Services` },
        { name: "description", content: loaderData?.category.intro.slice(0, 155) ?? "" },
        { property: "og:title", content: `${name} Epoxy Applications, Blanco Pro` },
        { property: "og:description", content: loaderData?.category.intro.slice(0, 155) ?? "" },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: "https://blancopros.com/og-image.jpg" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${name} Epoxy Applications, Blanco Pro` },
        { name: "twitter:description", content: loaderData?.category.intro.slice(0, 155) ?? "" },
        { name: "twitter:image", content: "https://blancopros.com/og-image.jpg" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: loaderData
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://blancopros.com/" },
                  { "@type": "ListItem", position: 2, name: "Usage", item: "https://blancopros.com/usage" },
                  { "@type": "ListItem", position: 3, name: loaderData.category.name, item: url },
                ],
              }),
            },
          ]
        : [],
    };
  },
  notFoundComponent: () => (
    <div className="pt-40 pb-20 text-center px-4">
      <h1 className="font-display text-4xl font-bold mb-4">Category not found</h1>
      <Link to="/usage" className="text-foreground underline">Back to Usage</Link>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="pt-40 pb-20 text-center px-4">
      <h1 className="font-display text-4xl font-bold mb-4">Something went wrong</h1>
      <button onClick={reset} className="text-foreground underline">Try again</button>
    </div>
  ),
  component: CategoryPage,
});

function CategoryPage() {
  const { category } = Route.useLoaderData();

  return (
    <>
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-hero">
        <div className="max-w-7xl mx-auto">
          <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 text-xs uppercase tracking-[0.25em] text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/usage" className="hover:text-foreground transition-colors">Usage</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground">{category.name}</span>
          </nav>
          <Reveal>
            <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">{category.tagline}</div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.05] mb-6">
              {category.name} <span className="bg-gradient-gold bg-clip-text text-transparent">Epoxy Applications</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl">{category.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {category.items.map((item: typeof category.items[number], idx: number) => (
            <Reveal key={item.slug} delay={idx * 60}>
              <Link
                to="/usage/$category/$item"
                params={{ category: category.slug, item: item.slug }}
                className="group block h-full rounded-2xl border border-border bg-card p-6 shadow-soft hover:shadow-elegant hover:-translate-y-1 transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-display text-xl font-bold">{item.name}</h3>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all" />
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.blurb}</p>
              </Link>
            </Reveal>
          ))}
        </div>

        <div className="max-w-7xl mx-auto mt-16 flex flex-wrap items-center justify-center gap-3">
          <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground mr-2">Other categories:</span>
          {usageCategories.filter((c) => c.slug !== category.slug).map((c) => (
            <Link
              key={c.slug}
              to="/usage/$category"
              params={{ category: c.slug }}
              className="text-sm px-4 py-2 rounded-full border border-border hover:bg-foreground hover:text-background transition-colors"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      <CtaBanner />
    </>
  );
}
